import {
    AccessTime as AccessTimeIcon,
    CalendarToday as CalendarTodayIcon,
    CheckCircle as CheckCircleIcon,
    Check as CheckIcon,
    Flag as FlagIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import {
    Avatar,
    AvatarGroup,
    Box,
    Card,
    CardContent,
    Chip,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import React, { useState } from 'react';

const truncateString = (str, maxLength) => {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return `${str.slice(0, maxLength)}...`;
};

const formatDate = (date) => {
    if (!date) return '';
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
};

const stringToColor = (str) => {
    if (!str) return '#999';
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return `#${'00000'.substring(0, 6 - c.length)}${c}`;
};

const PublicCardItem = React.memo(function PublicCardItem({ item, displayFilters, onOpen }) {
    const [showFullChecklist, setShowFullChecklist] = useState(false);

    const checklistItemsTotal = (item?.tasks || []).reduce(
        (total, task) => total + (task.checklists?.length || 0),
        0
    );
    const checklistItemsDone = (item?.tasks || []).reduce(
        (total, task) => total + (task.checklists?.filter((c) => c.isCompleted).length || 0),
        0
    );
    const checklistPct = checklistItemsTotal > 0 ? (checklistItemsDone / checklistItemsTotal) * 100 : 0;

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 1.5,
                padding: '6px',
                boxShadow: theme =>
                    theme.palette.mode === 'light'
                        ? '0 2px 4px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.07)'
                        : '0 1px 2px rgba(0,0,0,0.07)',
                position: 'relative',
                overflow: 'visible',
                transition: 'all 0.2s ease-in-out',
                borderColor: theme =>
                    theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)',
                backgroundColor: theme =>
                    theme.palette.mode === 'light' ? '#FFFFFF' : '#161c24',
                width: '100%',
                maxWidth: '280px',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: theme =>
                        theme.palette.mode === 'light'
                            ? '0 4px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)'
                            : '0 2px 6px rgba(0,0,0,0.12)',
                },
            }}
            onClick={() => onOpen && onOpen(item.id)}
        >
            <CardContent sx={{ p: 1, py: 0.75, '&:last-child': { pb: 0.75 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 0.25, alignItems: 'flex-start' }}>
                    <Typography
                        variant="body1"
                        component="div"
                        fontWeight="600"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            wordBreak: 'break-word',
                            flexGrow: 1,
                            fontSize: '0.9rem',
                            lineHeight: 1.3,
                            color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.87)' : '#fff',
                        }}
                    >
                        {item.title}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {displayFilters?.showPriorityName && item?.kanbanPriorityLevel && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                borderRadius: '7px',
                                bgcolor: `${item.kanbanPriorityLevel.color}22`,
                                px: 0.75,
                                py: 0.25,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: item.kanbanPriorityLevel.color,
                                    mr: 0.5,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    color: item.kanbanPriorityLevel.color,
                                }}
                            >
                                {item.kanbanPriorityLevel.title}
                            </Typography>
                        </Box>
                    )}

                    {displayFilters?.showCardAuthorship && item?.creator && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                                borderRadius: '7px',
                                px: 0.75,
                                py: 0.25,
                                border: '1px solid',
                                borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 14, color: theme => theme.palette.success.main }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                }}
                            >
                                Criado por: {truncateString(item.creator.name, 20)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75, mb: 0.75 }}>
                    {item.createdAt && (
                        <Chip
                            size="small"
                            icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                            label={formatDate(item.createdAt)}
                            variant="outlined"
                            sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                '.MuiChip-label': { px: 0.75 },
                                fontWeight: 500,
                            }}
                        />
                    )}
                </Box>

                {displayFilters?.showTags && item?.tags?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                        {item.tags.map((label) => (
                            <Box
                                key={label.id}
                                sx={{
                                    bgcolor: `${label.color}15`,
                                    color: label.color,
                                    borderRadius: '7px',
                                    px: 0.75,
                                    py: 0.25,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    border: '1px solid',
                                    borderColor: `${label.color}30`,
                                }}
                            >
                                {label.title}
                            </Box>
                        ))}
                    </Box>
                )}

                {item.description && (
                    <Typography
                        variant="body2"
                        color={theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.65)' : 'text.secondary'}
                        sx={{
                            mb: 0.75,
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {item.description}
                    </Typography>
                )}

                {checklistItemsTotal > 0 && (
                    <Box sx={{ mb: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                }}
                            >
                                {checklistItemsDone}/{checklistItemsTotal}
                            </Typography>
                            <Box
                                sx={{
                                    height: 3,
                                    width: '100%',
                                    bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
                                    borderRadius: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        width: `${checklistPct}%`,
                                        bgcolor: theme =>
                                            checklistItemsDone === checklistItemsTotal
                                                ? theme.palette.success.main
                                                : theme.palette.primary.main,
                                        borderRadius: 2,
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ pl: 0.25 }}>
                            {item.tasks
                                .flatMap((task) =>
                                    (task.checklists || []).map((checkItem) => ({
                                        ...checkItem,
                                        taskTitle: task.title,
                                    }))
                                )
                                .slice(0, showFullChecklist ? undefined : 1)
                                .map((checkItem) => (
                                    <Box
                                        key={checkItem.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 0.75,
                                            mb: 0.5,
                                            opacity: checkItem.isCompleted ? 0.65 : 1,
                                        }}
                                    >
                                        {checkItem.isCompleted ? (
                                            <CheckCircleIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: theme => theme.palette.success.main,
                                                    mt: 0.25,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : (
                                            <CheckIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.3)' : 'text.disabled',
                                                    mt: 0.25,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textDecoration: checkItem.isCompleted ? 'line-through' : 'none',
                                                color: checkItem.isCompleted
                                                    ? (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.4)' : 'text.disabled'
                                                    : (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'text.primary',
                                                fontSize: '0.7rem',
                                                lineHeight: 1.4,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {checkItem.description}
                                        </Typography>
                                    </Box>
                                ))}
                        </Box>

                        {checklistItemsTotal > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
                                <Typography
                                    variant="caption"
                                    color="primary.main"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFullChecklist(!showFullChecklist);
                                    }}
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                        ...(showFullChecklist
                                            ? {
                                                  px: 1,
                                                  py: 0.25,
                                                  bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.15)',
                                                  borderRadius: 1,
                                                  '&:hover': {
                                                      bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.25)',
                                                  },
                                              }
                                            : {
                                                  '&:hover': { textDecoration: 'underline' },
                                              }),
                                    }}
                                >
                                    {showFullChecklist
                                        ? 'Mostrar menos'
                                        : `+${checklistItemsTotal - 1} mais`}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mt: 0.75,
                        borderTop: theme => `1px solid ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.04)'}`,
                        pt: 0.75,
                    }}
                >
                    {item?.assigned?.length > 0 && (
                        <AvatarGroup
                            max={3}
                            sx={{
                                '& .MuiAvatar-root': {
                                    width: 24,
                                    height: 24,
                                    fontSize: '0.75rem',
                                    border: theme => `2px solid ${theme.palette.mode === 'light' ? '#fff' : '#161c24'}`,
                                    boxShadow: theme => theme.palette.mode === 'light' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                },
                            }}
                        >
                            {item.assigned.map((u) => (
                                <Tooltip key={u.id} title={u.name || ''}>
                                    <Avatar sx={{ bgcolor: stringToColor(u.name) }}>
                                        {(u.name || '?').slice(0, 1).toUpperCase()}
                                    </Avatar>
                                </Tooltip>
                            ))}
                        </AvatarGroup>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
});

export default PublicCardItem;
